import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { AuthGuard } from './components/AuthGuard';
// Sync — главные точки входа, первый экран ускоряется без лишних chunks.
import { BlockedPage } from './pages/blocked';
import { HomePage } from './pages/home';
import { LoginRequiredPage } from './pages/login-required';

// Lazy — догружаются при переходе. Каждая страница становится отдельным
// chunk'ом, начальный bundle резко уменьшается.
const ChatPage = lazy(() => import('./pages/chat').then((m) => ({ default: m.ChatPage })));
const DemoPage = lazy(() => import('./pages/demo').then((m) => ({ default: m.DemoPage })));
const ModulePage = lazy(() => import('./pages/module').then((m) => ({ default: m.ModulePage })));
const PaymentsPage = lazy(() =>
  import('./pages/payments').then((m) => ({ default: m.PaymentsPage })),
);
const PaymentDetailPage = lazy(() =>
  import('./pages/payments/detail').then((m) => ({ default: m.PaymentDetailPage })),
);
const PaymentsDonePage = lazy(() =>
  import('./pages/payments/done').then((m) => ({ default: m.PaymentsDonePage })),
);
const ResourcesPage = lazy(() =>
  import('./pages/resources').then((m) => ({ default: m.ResourcesPage })),
);
const ServicesPage = lazy(() =>
  import('./pages/services').then((m) => ({ default: m.ServicesPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/settings').then((m) => ({ default: m.SettingsPage })),
);
const TeamPage = lazy(() => import('./pages/team').then((m) => ({ default: m.TeamPage })));
const TimelinePage = lazy(() =>
  import('./pages/timeline').then((m) => ({ default: m.TimelinePage })),
);
const NotesPage = lazy(() => import('./pages/notes').then((m) => ({ default: m.NotesPage })));
const BindPage = lazy(() => import('./pages/bind').then((m) => ({ default: m.BindPage })));

// Один QueryClient на приложение. Cache TTL — 30 сек: данные кабинета не
// меняются часто, но и устаревшими держать не хочется.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageFallback() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600" />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* --- Public --- */}
          <Route path="/login-required" element={<LoginRequiredPage />} />
          <Route path="/blocked" element={<BlockedPage />} />
          <Route path="/demo" element={<DemoPage />} />

          {/* Привязка Telegram (B-1984): нужен вход, но НЕ гейт привязки. */}
          <Route
            path="/bind"
            element={
              <AuthGuard>
                <BindPage />
              </AuthGuard>
            }
          />

          {/* --- Protected --- */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <HomePage />
              </AuthGuard>
            }
          />
          <Route
            path="/modules/:slug"
            element={
              <AuthGuard>
                <ModulePage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <SettingsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/team"
            element={
              <AuthGuard>
                <TeamPage />
              </AuthGuard>
            }
          />
          <Route
            path="/payments"
            element={
              <AuthGuard>
                <PaymentsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/payments/done"
            element={
              <AuthGuard>
                <PaymentsDonePage />
              </AuthGuard>
            }
          />
          <Route
            path="/payments/:id"
            element={
              <AuthGuard>
                <PaymentDetailPage />
              </AuthGuard>
            }
          />
          <Route
            path="/timeline"
            element={
              <AuthGuard>
                <TimelinePage />
              </AuthGuard>
            }
          />
          <Route
            path="/chat"
            element={
              <AuthGuard>
                <ChatPage />
              </AuthGuard>
            }
          />
          <Route
            path="/resources"
            element={
              <AuthGuard>
                <ResourcesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/notes"
            element={
              <AuthGuard>
                <NotesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services"
            element={
              <AuthGuard>
                <ServicesPage />
              </AuthGuard>
            }
          />

          {/* --- 404 --- */}
          <Route
            path="*"
            element={
              <main className="flex min-h-[100dvh] items-center justify-center px-4">
                <div className="text-center">
                  <h1 className="mb-2 text-2xl font-semibold">Страница не найдена</h1>
                  <p className="text-slate-600">Проверь адрес или вернись на главную.</p>
                </div>
              </main>
            }
          />
        </Routes>
      </Suspense>
    </QueryClientProvider>
  );
}
